import React, { useCallback, useEffect, useState } from "react";
import { adminApi, API } from "@/lib/adminApi";
import { AdminPageHeader, Card, Input, Select, Btn, Spinner, ErrorBanner, Pagination, EmptyState, Tag } from "./_shared";
import { Search, Download, Trash2, Check, RotateCcw, Mail } from "lucide-react";

export const Contact = () => {
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const limit = 25;
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [actingId, setActingId] = useState(null);
  const [open, setOpen] = useState({}); // id -> bool (expanded full message)

  const load = useCallback(async () => {
    setErr(null);
    try {
      const params = { status, skip, limit };
      if (q) params.q = q;
      const r = await adminApi.get("/admin/contact", { params });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load contact messages.");
    }
  }, [status, q, skip]);

  useEffect(() => { load(); }, [load]);

  const toggleRead = async (id, current) => {
    setActingId(id);
    try {
      await adminApi.post(`/admin/contact/${id}/read`, { status: current === "new" ? "read" : "new" });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Action failed.");
    } finally { setActingId(null); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this message? This cannot be undone.")) return;
    setActingId(id);
    try {
      await adminApi.delete(`/admin/contact/${id}`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Delete failed.");
    } finally { setActingId(null); }
  };

  const exportHref = `${API}/admin/contact/export`;

  return (
    <div className="space-y-5" data-testid="contact-page">
      <AdminPageHeader
        eyebrow="GET IN TOUCH"
        title="Contact messages"
        sub="Submissions from the public 'Get in touch' form on /about. Spam-filtered via Cloudflare Turnstile + per-IP rate limit."
        action={
          <a
            href={exportHref}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 uppercase font-extrabold tracking-[0.2em] hover:opacity-90 transition-opacity"
            style={{ background: "#FF9933", color: "#0A0A0A", border: "2px solid #FF9933", padding: "0.55rem 0.9rem", fontSize: "0.7rem" }}
            data-testid="contact-export"
          >
            <Download size={14} strokeWidth={3} /> Export CSV
          </a>
        }
      />

      <Card>
        <div className="grid md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-7">
            <label className="block text-[10px] uppercase font-bold tracking-[0.3em] text-neutral-400 mb-2">Search</label>
            <div className="relative">
              <Input
                value={q}
                onChange={(e) => { setSkip(0); setQ(e.target.value); }}
                placeholder="Search name, email, or message text…"
                data-testid="contact-search"
                className="pl-9"
              />
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" strokeWidth={3} />
            </div>
          </div>
          <div className="md:col-span-3">
            <label className="block text-[10px] uppercase font-bold tracking-[0.3em] text-neutral-400 mb-2">Status</label>
            <Select value={status} onChange={(e) => { setSkip(0); setStatus(e.target.value); }} data-testid="contact-status-filter">
              <option value="all">All</option>
              <option value="new">Unread</option>
              <option value="read">Read</option>
            </Select>
          </div>
          <div className="md:col-span-2 text-xs uppercase font-bold tracking-[0.3em] text-neutral-400 md:pb-2.5">
            Unread: <span style={{ color: "#FF9933" }} data-testid="contact-unread-count">{(data?.unread ?? 0).toLocaleString()}</span>
          </div>
        </div>
      </Card>

      {err && <ErrorBanner message={err} />}
      {!data && !err && <Spinner />}

      {data && data.messages.length === 0 && (
        <EmptyState title="No contact messages match." sub="Submissions from the public form appear here." />
      )}

      {data && data.messages.length > 0 && (
        <Card>
          <ul className="divide-y divide-[#1f1f1f]" data-testid="contact-list">
            {data.messages.map((m) => {
              const isOpen = !!open[m.id];
              const preview = (m.message || "").length > 220 && !isOpen
                ? m.message.slice(0, 220) + "…"
                : m.message;
              return (
                <li key={m.id} className="py-4" data-testid={`contact-row-${m.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <Mail size={14} strokeWidth={3} style={{ color: m.status === "new" ? "#FF9933" : "#737373" }} />
                        <p className="text-sm font-extrabold uppercase tracking-[0.1em]" data-testid={`contact-name-${m.id}`}>{m.name}</p>
                        <Tag color={m.status === "new" ? "#FF9933" : "#737373"} testId={`contact-status-${m.id}`}>
                          {m.status === "new" ? "Unread" : "Read"}
                        </Tag>
                        <span className="text-[10px] uppercase font-bold tracking-[0.3em] text-neutral-500">
                          {m.created_at?.slice(0, 16).replace("T", " ")}
                        </span>
                      </div>
                      <p className="text-xs text-neutral-400">
                        <a
                          href={`mailto:${m.email}?subject=${encodeURIComponent("Re: your message to Kerala Speaks")}`}
                          className="underline hover:text-[#FF9933]"
                          data-testid={`contact-email-${m.id}`}
                        >
                          {m.email}
                        </a>
                        {m.ip && <span className="ml-2 text-neutral-600">· {m.ip}</span>}
                      </p>
                      <p className="text-sm leading-relaxed mt-3 whitespace-pre-wrap" data-testid={`contact-message-${m.id}`}>
                        {preview}
                      </p>
                      {(m.message || "").length > 220 && (
                        <button
                          type="button"
                          onClick={() => setOpen((o) => ({ ...o, [m.id]: !isOpen }))}
                          className="text-[10px] uppercase font-extrabold tracking-[0.3em] mt-2 hover:underline"
                          style={{ color: "#FF9933" }}
                          data-testid={`contact-expand-${m.id}`}
                        >
                          {isOpen ? "Show less" : "Read full"} →
                        </button>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      <Btn
                        variant="ghost"
                        onClick={() => toggleRead(m.id, m.status)}
                        disabled={actingId === m.id}
                        data-testid={`contact-toggle-read-${m.id}`}
                      >
                        {m.status === "new"
                          ? <><Check size={12} strokeWidth={3} /> Mark read</>
                          : <><RotateCcw size={12} strokeWidth={3} /> Mark unread</>}
                      </Btn>
                      <Btn
                        variant="danger"
                        onClick={() => remove(m.id)}
                        disabled={actingId === m.id}
                        data-testid={`contact-delete-${m.id}`}
                      >
                        <Trash2 size={12} strokeWidth={3} /> Delete
                      </Btn>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          <Pagination skip={skip} limit={limit} total={data.total || 0} onChange={setSkip} />
        </Card>
      )}
    </div>
  );
};

export default Contact;
